import {useFeatureAppEnv} from '@smart/feature-app';
import {DynamicMediaPicture, TextLink} from '@smart/web-components';
import classnames from 'classnames';
import React, {useMemo} from 'react';
import {useProductSearch} from '../../../feature-app/contexts/product-search-context';
import {createLinkProps} from '../../utils/create-link-props';
import styles from './banner.module.scss';

const baseClass = `banner-wrapper`;

interface BannerProps {
  template: 'offer' | 'campaign';
  position?: number;
  title?: string;
  fromLabel?: string;
  price?: string;
  leasingCycleLabel?: string;
  imageUrl?: string;
  description?: string;
  linkLabel?: string;
  linkDestination?: any;
  backgroundColorOption?:
    | 'premium #1'
    | 'premium #3'
    | 'brabus'
    | 'pulse'
    | 'pro+'
    | 'pro,pure+,pure';
  leasingPriceReference: string;
}

const Banner = ({
  template = `offer`,
  title,
  fromLabel,
  price,
  leasingCycleLabel,
  description,
  linkLabel,
  linkDestination,
  imageUrl,
  backgroundColorOption,
  leasingPriceReference,
}: BannerProps) => {
  const getBackgroundColor = useMemo(() => {
    const colorMapping = {
      'premium #1': `#BCC9C4`,
      'premium #3': `#AAC1CD`,
      'brabus': `#3A3C3F`,
      'pulse': `#799DC9`,
      'pro+': `#DED774`,
      'pro,pure+,pure': `#E4E8EB`,
    };

    if (!backgroundColorOption || !colorMapping[backgroundColorOption]) {
      return `#BCC9C4`;
    }

    return colorMapping[backgroundColorOption];
  }, [backgroundColorOption]);

  const Link = () => {
    const {
      featureServices: {'s2:history': historyService},
    } = useFeatureAppEnv();
    const {applyFilter} = useProductSearch();
    if (linkDestination && linkLabel) {
      return (
        <TextLink
          size="200"
          className={styles.link}
          variant={`button-secondary`}
          linkText={linkLabel || ``}
          {...createLinkProps({
            destination: linkDestination,
            historyService,
            applyFilter,
          })}
        />
      );
    }
    return null;
  };
  return (
    <div className={styles[baseClass]}>
      <div
        className={classnames(styles[`${baseClass}__image-wrapper`], {
          [styles[`${baseClass}__offer-template`] as string]:
            !!backgroundColorOption,
        })}
        style={{
          backgroundColor: getBackgroundColor,
        }}
      >
        <DynamicMediaPicture
          className={classnames({
            [styles[`campaign__picture`] as string]: !backgroundColorOption,
          })}
          imageUrl={imageUrl || ``}
          imageConfigs={[
            {
              aspectRatio: `1-1`,
              sizes: `100vw`,
            },
          ]}
          imageAltText={``}
          hasImageTransparency={!!backgroundColorOption}
          initialIsInView
          noLoadingLogo
          isObjectFit
          fetchPriority="high"
        />
      </div>
      <div className={styles[`${baseClass}__content-wrapper`]}>
        {template === `offer` ? (
          <>
            <div className={styles[`from-label`]}>{fromLabel}</div>
            <div className={styles[`price-wrapper`]}>
              <div className={styles[`price`]}>{price}</div>
              <div className={styles[`leasing-cycle`]}>
                {leasingCycleLabel}
                <sup>{leasingPriceReference}</sup>
              </div>
            </div>
          </>
        ) : (
          <div className={styles.title}>{title}</div>
        )}
        <div className={styles[`sub-line`]}>{description}</div>
        <Link />
      </div>
    </div>
  );
};

export default React.memo(Banner);
